import React, { useEffect, useRef } from 'react';
import { useTranslation } from 'react-i18next';
import { X } from 'lucide-react';

const TermsModal = ({ titleKey, bodyKey, onClose }) => {
  const { t, i18n } = useTranslation();
  const isRtl = i18n.language === 'ar';
  const closeButtonRef = useRef(null);
  const overlayRef = useRef(null);

  useEffect(() => {
    const handleEscape = (e) => {
      if (e.key === 'Escape') {
        e.stopPropagation();
        onClose();
      }
    };
    document.addEventListener('keydown', handleEscape);

    const previousOverflow = document.body.style.overflow;
    document.body.style.overflow = 'hidden';

    if (closeButtonRef.current) {
      closeButtonRef.current.focus();
    }

    return () => {
      document.removeEventListener('keydown', handleEscape);
      document.body.style.overflow = previousOverflow;
    };
  }, [onClose]);

  const handleOverlayMouseDown = (e) => {
    if (e.target === overlayRef.current) {
      onClose();
    }
  };

  const sections = t(bodyKey, { returnObjects: true });
  const sectionList = Array.isArray(sections) ? sections : [];

  return (
    <div
      ref={overlayRef}
      className="modal-overlay modal-overlay-front"
      onMouseDown={handleOverlayMouseDown}
      role="dialog"
      aria-modal="true"
      aria-labelledby="terms-modal-title"
      dir={isRtl ? 'rtl' : 'ltr'}
    >
      <div
        className={`modal-content max-w-2xl ${isRtl ? 'text-right font-arabic' : 'text-left'}`}
        dir={isRtl ? 'rtl' : 'ltr'}
        style={{ textAlign: isRtl ? 'right' : 'left' }}
      >
        <div
          className="sticky top-0 z-10 flex items-center justify-between px-6 py-4 border-b"
          style={{ backgroundColor: 'var(--card)', borderColor: 'var(--border)' }}
        >
          <h2
            id="terms-modal-title"
            className="text-xl font-bold"
            style={{ color: 'var(--text-primary)' }}
          >
            {t(titleKey)}
          </h2>
          <button
            ref={closeButtonRef}
            type="button"
            onClick={onClose}
            aria-label={t('terms.close')}
            className="p-2 rounded-md hover:bg-muted/60 transition-colors"
            style={{ color: 'var(--text-secondary)' }}
          >
            <X className="h-5 w-5" />
          </button>
        </div>

        <div
          className={`px-6 py-5 space-y-5 ${isRtl ? 'text-right' : 'text-left'}`}
          dir={isRtl ? 'rtl' : 'ltr'}
          style={{ textAlign: isRtl ? 'right' : 'left' }}
        >
          {sectionList.map((section, index) => (
            <section key={index} dir={isRtl ? 'rtl' : 'ltr'}>
              <h3
                className="text-base font-semibold mb-1"
                style={{ color: 'var(--text-primary)', textAlign: isRtl ? 'right' : 'left' }}
              >
                {section.heading}
              </h3>
              <p
                className="text-sm leading-relaxed"
                style={{ color: 'var(--text-secondary)', textAlign: isRtl ? 'right' : 'left' }}
              >
                {section.body}
              </p>
            </section>
          ))}

          <p
            className="text-xs italic pt-2"
            style={{ color: 'var(--text-muted)', textAlign: isRtl ? 'right' : 'left' }}
          >
            {t('terms.lastUpdatedLabel')}: {t('terms.lastUpdatedValue')}
          </p>
        </div>

        <div
          className="sticky bottom-0 px-6 py-4 border-t flex justify-end"
          style={{ backgroundColor: 'var(--card)', borderColor: 'var(--border)' }}
        >
          <button
            type="button"
            onClick={onClose}
            className="glass-button px-6 py-2 text-sm font-semibold"
          >
            {t('terms.close')}
          </button>
        </div>
      </div>
    </div>
  );
};

export default TermsModal;
